import {PhoneHQAvatar} from 'ch3-ui-lib';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useSelector} from "react-redux";
import {selectUserId} from "../../store/features/Auth/authSlice";
import {useLazyResolveUserIdsQuery, UserElement} from "../../store/features/Users/userService";
import {getUserStatus} from "../../utilis/getStatus.ts";
import {Camera, Check, ChevronLeft, Lock, Users, X} from "lucide-react";
import {Search} from "lucide-react";
import {hexToEmoji} from "../Emoji/emoji";
import {convertFromISOFormat} from "../../utilis/timeUtilis";
import {checkStatusExpiration} from "../../utilis/statusUtilis.ts";
import {useIdResolver} from "../../hooks/useIdResolver.ts";
import {useStatusSubscription} from "../../hooks/useStatusSubscription";
import googleCalendarIcon from "../../assets/google-calendar.svg";
import AvatarPicker from "./AvatarPicker.tsx";

const GROUP_NAME_MAX = 50;

export interface UserWithMatrixId extends UserElement {
    matrixId: string;
}

interface CreateRoomModalProps {
    users: UserElement[];
    onClose: () => void;
    onRoomCreate: (matrixIds: string[], roomName?: string) => void;
    isEditing?: boolean;
    /** `avatar` is set on the group once Matrix has created it (D-CHAT-03/S03). */
    startPreparingChat?: (users: UserWithMatrixId[], optionalGroupName?: string, forceGroupMode?: boolean, avatar?: File) => void;
}

const CreateRoomModal: React.FC<CreateRoomModalProps> = ({
                                                             users,
                                                             onClose,
                                                             onRoomCreate,
                                                             isEditing = false,
                                                             startPreparingChat
                                                         }) => {
    const [selectedUsers, setSelectedUsers] = useState<UserElement[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isGroup, setIsGroup] = useState(false);
    // A group is made in two steps: pick people, then name and avatar.
    const [groupStep, setGroupStep] = useState<'members' | 'settings'>('members');
    const [groupAvatar, setGroupAvatar] = useState<File | null>(null);
    const [showGroupAvatarPicker, setShowGroupAvatarPicker] = useState(false);

    const [optionalGroupName, setOptionalGroupName] = useState('');


    const currentUserId = useSelector(selectUserId);
    const userIds = useMemo(() => users.map(user => user.id), [users]);
    const userIdsAsStrings = useMemo(() => userIds.map(id => String(id)), [userIds]);

    // Use Redux-based ID resolver
    const { getMatrixId, isLoading: isResolvingIds } = useIdResolver();
    const [resolveUserIds, { isFetching: isResolvingSelectedUserIds }] = useLazyResolveUserIdsQuery();

    const { statuses } = useStatusSubscription({
        userIds: userIdsAsStrings
    });

    const chatUsers = useMemo(() => {
        return users?.reduce((acc: any[], user: any) => {
            const status = statuses.find(status => status.userId === user.id);
            const isUninitialized = user.chatInitializedAt == null;
            if (user.id != currentUserId && (status || isUninitialized)) {
                acc.push({
                    ...status,
                    ...user,
                    userId: user.id,
                });
            }
            return acc;
        }, []);
    }, [users, statuses, currentUserId]);

    const filteredUsers = chatUsers.filter(user => {
        const searchLower = searchTerm.toLowerCase();
        const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();

        return Number(user.id) !== Number(currentUserId) &&
            (user.firstName.toLowerCase().includes(searchLower) ||
                user.lastName.toLowerCase().includes(searchLower) ||
                user.email.toLowerCase().includes(searchLower) ||
                fullName.includes(searchLower));
    });

    const isUserUninitialized = (user: UserElement) => user.chatInitializedAt == null;

    const uninitializedTooltip = "Can't invite yet — user hasn't completed their first-time setup.";

    const handleCheckboxChange = (user: UserElement) => {
        if (isUserUninitialized(user)) return;
        setSelectedUsers(prev => {
            const isAlreadySelected = prev.find(selected => selected.id === user.id);
            if (isAlreadySelected) {
                return prev.filter(selected => selected.id !== user.id);
            } else {
                return [...prev, user];
            }
        });
    };

    const getUserDisplayName = (user: UserElement) => {
        return `${user.firstName} ${user.lastName}`;
    };

    const isCustomStatusExpired = (user: any) => {
        if (!user.customUserStatus?.dueDate) return true;

        let isExpired = true;
        checkStatusExpiration(user, (expired: boolean) => {
            isExpired = expired;
        });
        return isExpired;
    };

    const getStatusDisplayInfo = (user: any) => {
        const hasCustomStatus = user.customUserStatus?.name && !isCustomStatusExpired(user);
        const hasCustomIcon = user.customUserStatus?.icon && !isCustomStatusExpired(user);
        const hasMeetingStatus = user.meetingUserStatus?.status === 'IN_MEETING';
        const meetingExpires = hasMeetingStatus
            ? (() => {
                const {dateString, timeString} = convertFromISOFormat(user.meetingUserStatus?.dateTo || '');
                return dateString ? `${dateString}, ${timeString}` : timeString;
            })()
            : '';
        const meetingStatusName = hasMeetingStatus
            ? `In a meeting${meetingExpires ? ` until ${meetingExpires}` : ''}`
            : null;
        const statusName = [
            hasCustomStatus ? user.customUserStatus.name : null,
            meetingStatusName
        ].filter(Boolean).join(' · ') || null;

        if (statusName) {
            const { dateString, timeString } = convertFromISOFormat(user.customUserStatus?.dueDate || '');
            const expires = dateString ? `${dateString}, ${timeString}` : timeString;

            return {
                hasStatus: true,
                hasMeetingStatus,
                statusName,
                statusIcon: hasCustomIcon ? user.customUserStatus.icon : null,
                expires: expires
            };
        }

        return {
            hasStatus: false,
            hasMeetingStatus: false,
            statusName: null,
            statusIcon: null,
            expires: null
        };
    };

    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (groupStep === 'settings' && !showGroupAvatarPicker && inputRef.current) {
            inputRef.current.focus();
        }
    }, [groupStep, showGroupAvatarPicker]);

    const resolveUsersWithMatrixIds = async (usersToResolve: UserElement[]): Promise<UserWithMatrixId[] | null> => {
        const unresolvedUserIds = usersToResolve
            .filter(user => !getMatrixId(String(user.id)))
            .map(user => String(user.id));
        let resolvedMatrixIds: Record<string, string> = {};

        if (unresolvedUserIds.length > 0) {
            try {
                resolvedMatrixIds = await resolveUserIds({ userIds: unresolvedUserIds }).unwrap();
            } catch (error) {
                console.error('Failed to resolve Matrix IDs for selected users', error);
                alert('Unable to create the group because user chat IDs could not be loaded. Please try again.');
                return null;
            }
        }

        const usersWithMatrixIds = usersToResolve.map(user => ({
            ...user,
            matrixId: getMatrixId(String(user.id)) || resolvedMatrixIds[String(user.id)] || ''
        }));
        const usersWithoutMatrixIds = usersWithMatrixIds.filter(user => !user.matrixId);

        if (usersWithoutMatrixIds.length > 0) {
            console.error('Some users do not have Matrix IDs', usersWithoutMatrixIds.map(user => user.id));
            alert('Unable to create the group because one or more selected users are not ready for chat.');
            return null;
        }

        return usersWithMatrixIds;
    };

    const handleCreateGroup = async () => {
        if(!startPreparingChat) return;

        const usersWithMatrixIds = await resolveUsersWithMatrixIds(selectedUsers);
        if (!usersWithMatrixIds) return;

        startPreparingChat(usersWithMatrixIds, optionalGroupName, true, groupAvatar ?? undefined);
        onClose();
    };

    const handleSelectAll = () => {
        setSelectedUsers(chatUsers.filter((u: UserElement) => !isUserUninitialized(u)));
    };

    const avatarPreview = useMemo(() => (groupAvatar ? URL.createObjectURL(groupAvatar) : null), [groupAvatar]);
    useEffect(() => () => {
        if (avatarPreview) URL.revokeObjectURL(avatarPreview);
    }, [avatarPreview]);

    const membersPlaceholder = selectedUsers.map(getUserDisplayName).join(', ');
    const inGroupFlow = isGroup || isEditing;

    const startDirectChat = (user: UserElement) => {
        if (!startPreparingChat) {
            onClose();
            return;
        }
        const matrixId = getMatrixId(String(user.id));
        if (matrixId) {
            startPreparingChat([{...user, matrixId}]);
            onClose();
        } else {
            console.error('Matrix ID not resolved for user:', user.id, 'Still resolving:', isResolvingIds);
            if (!isResolvingIds) {
                alert(`Unable to start chat. Please try again.`);
                onClose();
            }
        }
    };

    const renderStatus = (user: UserElement) => {
        const statusInfo = getStatusDisplayInfo(user);
        if (!statusInfo.hasStatus) return null;
        return (
            <span className="ml-2 inline-flex min-w-0 items-center gap-1 font-normal text-zinc-300">
                {statusInfo.statusIcon
                    ? hexToEmoji(statusInfo.statusIcon, '!w-4 !h-4')
                    : statusInfo.hasMeetingStatus
                        ? <img className="h-4 w-4" src={googleCalendarIcon} alt=""/>
                        : null}
                <span className="truncate">{statusInfo.statusName}</span>
            </span>
        );
    };

    // D-CHAT-02/S01–S02: dark "New chat" picker.
    if (!inGroupFlow) {
        return (
            <div className="fixed inset-0 z-50 flex justify-center bg-scrim pt-[100px]" onClick={onClose}>
                <div role="dialog" aria-modal="true" aria-label="New chat" data-testid="chat.new-chat.dialog"
                     onClick={e => e.stopPropagation()}
                     className="phq-anim-pop flex h-fit max-h-[calc(100vh-140px)] w-[440px] max-w-[calc(100vw-2rem)] flex-col gap-2.5 rounded-2xl bg-zinc-950 p-4 text-zinc-50 shadow-overlay">
                    <div className="flex items-center justify-between text-sm font-semibold">
                        New chat
                        <button type="button" aria-label="Close" onClick={onClose}
                                className="flex h-7 w-7 items-center justify-center rounded-lg hover:bg-zinc-800">
                            <X size={16} aria-hidden="true" className="!p-0 !text-zinc-50"/>
                        </button>
                    </div>
                    <label className="flex h-9 items-center gap-2 rounded-[10px] border border-zinc-700 px-2.5 focus-within:border-zinc-400">
                        <Search size={16} aria-hidden="true" className="!p-0 !text-zinc-400"/>
                        <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} autoFocus
                               placeholder="Search name" aria-label="Search name"
                               className="min-w-0 flex-1 border-0 bg-transparent p-0 text-[13px] text-zinc-50 placeholder:text-zinc-400 outline-none focus:ring-0"/>
                    </label>
                    {!searchTerm && (
                        <button type="button" onClick={() => { setIsGroup(true); setGroupStep('members'); }}
                                data-testid="chat.new-chat.new-group"
                                className="flex h-10 items-center gap-2.5 rounded-lg bg-zinc-800 px-2 text-left text-[13px] font-medium hover:bg-zinc-700">
                            <Users size={16} aria-hidden="true" className="!p-0 !text-zinc-50"/>
                            New group
                        </button>
                    )}
                    {!searchTerm && <div className="px-2 text-[13px] text-zinc-400">People</div>}
                    <div className="-mx-1 flex min-h-0 flex-col overflow-y-auto px-1">
                        {filteredUsers.length === 0 ? (
                            <div className="px-2 py-4 text-center text-[13px] text-zinc-400">
                                {searchTerm ? 'No users found matching your search' : 'No users available'}
                            </div>
                        ) : filteredUsers.map(user => {
                            const uninitialized = isUserUninitialized(user);
                            return (
                                <button type="button" key={user.userId || user.id} disabled={uninitialized}
                                        onClick={() => startDirectChat(user)}
                                        {...(uninitialized ? {'data-tooltip-left': uninitializedTooltip} : {})}
                                        className="flex h-10 shrink-0 items-center gap-2.5 rounded-lg px-2 text-left text-[13px] font-medium text-zinc-50 hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-50">
                                    <PhoneHQAvatar src={user.avatarUrl} name={getUserDisplayName(user)} size="24px"/>
                                    <span className="flex min-w-0 flex-1 items-center truncate">
                                        <span className="shrink-0">{getUserDisplayName(user)}</span>
                                        {renderStatus(user)}
                                    </span>
                                    {uninitialized && <Lock size={14} aria-hidden="true" className="!p-0 !text-zinc-400"/>}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>
        );
    }

    const header = (title: string, onBack?: () => void, extra?: React.ReactNode) => (
        <div className="flex h-14 shrink-0 items-center gap-2 border-b border-zinc-700 px-3">
            {onBack ? (
                <button type="button" aria-label="Back" onClick={onBack}
                        className="flex h-8 w-8 items-center justify-center rounded-lg hover:bg-zinc-800">
                    <ChevronLeft size={18} aria-hidden="true" className="!p-0 !text-white"/>
                </button>
            ) : <span className="w-2"/>}
            <h2 className="m-0 flex-1 text-base font-semibold text-white">{title}</h2>
            {extra}
            <button type="button" aria-label="Close" onClick={onClose}
                    className="flex h-8 w-8 items-center justify-center rounded-lg hover:bg-zinc-800">
                <X size={18} aria-hidden="true" className="!p-0 !text-white"/>
            </button>
        </div>
    );

    // D-CHAT-03/S03: name and avatar, then Create.
    if (isGroup && groupStep === 'settings') {
        return (
            <div className="fixed inset-0 z-50 flex justify-center bg-scrim pt-20">
                <div role="dialog" aria-modal="true" aria-label="New group: group settings" data-testid="chat.new-group.settings"
                     className="phq-anim-pop flex h-fit w-[600px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-[20px] bg-zinc-900 text-white shadow-overlay">
                    {header('Group settings', () => setGroupStep('members'))}
                    {showGroupAvatarPicker ? (
                        <div className="bg-surface text-ink">
                            <AvatarPicker
                                onSave={file => { setGroupAvatar(file); setShowGroupAvatarPicker(false); }}
                                onCancel={() => setShowGroupAvatarPicker(false)}
                            />
                        </div>
                    ) : (<>
                        <div className="flex flex-col items-center gap-4 px-5 pb-1 pt-6">
                            {avatarPreview ? (
                                <img src={avatarPreview} alt="Group avatar" className="h-24 w-24 rounded-3xl object-cover"/>
                            ) : (
                                <span className="flex h-24 w-24 items-center justify-center rounded-3xl bg-[#2A1A4D] text-[#D9C9FF]">
                                    <Users size={40} aria-hidden="true" className="!p-0 !text-[#D9C9FF]"/>
                                </span>
                            )}
                            <button type="button" onClick={() => setShowGroupAvatarPicker(true)}
                                    data-testid="chat.new-group.change-avatar"
                                    className="inline-flex h-9 items-center gap-2 rounded-[10px] bg-zinc-700 px-4 text-sm font-semibold text-white hover:bg-zinc-600">
                                <Camera size={16} aria-hidden="true" className="!p-0 !text-white"/>
                                Change avatar
                            </button>
                        </div>
                        <div className="flex flex-col gap-1.5 px-5 pb-1 pt-3">
                            <label htmlFor="new-group-name" className="text-[13px] text-zinc-400">Group name</label>
                            <div className="flex h-10 items-center gap-2 rounded-[10px] bg-zinc-900 px-3 shadow-[inset_0_0_0_1px_#3F3F47] focus-within:shadow-[inset_0_0_0_2px_#B08CFF]">
                                <input id="new-group-name" ref={inputRef} type="text" value={optionalGroupName} maxLength={GROUP_NAME_MAX}
                                       onChange={e => setOptionalGroupName(e.target.value)}
                                       placeholder={membersPlaceholder}
                                       className="min-w-0 flex-1 border-0 bg-transparent p-0 text-sm text-white placeholder:text-zinc-400 outline-none focus:ring-0"/>
                                <span className="text-xs tabular-nums text-zinc-400">{optionalGroupName.length}/{GROUP_NAME_MAX}</span>
                            </div>
                            <span className="text-xs leading-[1.4] text-zinc-400">
                                Optional. Without a name the group is called after its members. Name and avatar can be changed later in the group panel.
                            </span>
                            <span className="mt-1 text-xs text-zinc-400">This chat is end-to-end encrypted</span>
                        </div>
                        <div className="flex flex-col px-5 pb-5 pt-4">
                            <button type="button" onClick={handleCreateGroup} disabled={isResolvingSelectedUserIds}
                                    data-testid="chat.new-group.create-btn"
                                    className="h-10 rounded-[10px] bg-brand text-sm font-semibold text-white hover:bg-brand-hover disabled:bg-brand-disabled">
                                {isResolvingSelectedUserIds ? 'Preparing…' : 'Create'}
                            </button>
                        </div>
                    </>)}
                </div>
            </div>
        );
    }

    // D-CHAT-03/S01–S02 (and "Add members" in the group panel): pick people.
    return (
        <div className="fixed inset-0 z-50 flex justify-center bg-scrim pt-20">
            <div role="dialog" aria-modal="true" aria-label={isEditing ? 'Add members' : 'New group'} data-testid="chat.new-group.members"
                 className="phq-anim-pop flex h-fit max-h-[calc(100vh-120px)] w-[600px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-[20px] bg-zinc-900 text-white shadow-overlay">
                {header(isEditing ? 'Add members' : 'New Group', isEditing ? undefined : () => { setIsGroup(false); setSelectedUsers([]); },
                    <button type="button" onClick={handleSelectAll}
                            className="h-8 rounded-[10px] bg-zinc-700 px-3 text-[13px] font-semibold text-white hover:bg-zinc-600">
                        Select all
                    </button>)}

                <div className="shrink-0 px-4 pb-1 pt-2">
                    <div className="mb-2 text-[13px] text-zinc-300">
                        Members{selectedUsers.length > 0 && ` (${selectedUsers.length})`}
                    </div>
                    {selectedUsers.length > 0 && (
                        <div className="flex gap-4 overflow-x-auto pb-2 pt-1">
                            {selectedUsers.map(user => (
                                <div key={user.id} className="relative flex w-14 shrink-0 flex-col items-center gap-1">
                                    <PhoneHQAvatar src={user.avatarUrl} name={getUserDisplayName(user)} size="40px"/>
                                    <button type="button" aria-label={`Remove ${getUserDisplayName(user)}`}
                                            onClick={() => setSelectedUsers(prev => prev.filter(s => s.id !== user.id))}
                                            className="absolute -top-1 right-1 flex h-4 w-4 items-center justify-center rounded-full bg-white">
                                        <X size={10} strokeWidth={3} aria-hidden="true" className="!p-0 !text-zinc-900"/>
                                    </button>
                                    <span className="line-clamp-2 text-center text-[11px] leading-[1.2] text-white">{getUserDisplayName(user)}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <label className="flex h-11 shrink-0 items-center gap-2.5 border-b border-zinc-700 px-4">
                    <Search size={16} aria-hidden="true" className="!p-0 !text-zinc-300"/>
                    <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                           placeholder="Search name" aria-label="Search name"
                           className="min-w-0 flex-1 border-0 bg-transparent p-0 text-sm text-white placeholder:text-zinc-300 outline-none focus:ring-0"/>
                </label>

                <div className="flex min-h-0 flex-1 flex-col overflow-y-auto py-1" role="group" aria-label="People">
                    {filteredUsers.length === 0 ? (
                        <div className="p-4 text-center text-sm text-zinc-400">
                            {searchTerm ? 'No users found matching your search' : 'No users available'}
                        </div>
                    ) : filteredUsers.map(user => {
                        const uninitialized = isUserUninitialized(user);
                        const checked = selectedUsers.some(s => s.id === user.id);
                        return (
                            <div key={user.userId || user.id} role="checkbox" aria-checked={checked} aria-disabled={uninitialized}
                                 tabIndex={uninitialized ? -1 : 0}
                                 onClick={() => handleCheckboxChange(user)}
                                 onKeyDown={e => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); handleCheckboxChange(user); } }}
                                 {...(uninitialized ? {'data-tooltip-left': uninitializedTooltip} : {})}
                                 className={`flex h-11 shrink-0 items-center gap-3 px-4 outline-none focus-visible:bg-zinc-800 ${uninitialized ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-zinc-800'}`}>
                                <PhoneHQAvatar src={user.avatarUrl} name={getUserDisplayName(user)} status={getUserStatus(user)} size="28px"/>
                                <div className="flex min-w-0 flex-1 items-center truncate text-sm font-medium text-white">
                                    <span className="shrink-0">{getUserDisplayName(user)}</span>
                                    {renderStatus(user)}
                                </div>
                                {uninitialized ? (
                                    <Lock size={16} aria-hidden="true" className="!p-0 !text-zinc-400"/>
                                ) : (
                                    <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md ${checked ? 'bg-brand' : 'shadow-[inset_0_0_0_2px_#71717B]'}`}>
                                        {checked && <Check size={14} strokeWidth={3} aria-hidden="true" className="!p-0 !text-white"/>}
                                    </span>
                                )}
                            </div>
                        );
                    })}
                </div>

                <div className="flex shrink-0 items-center gap-3 border-t border-zinc-700 px-4 py-3">
                    <span className="flex-1 text-xs text-zinc-400">This chat is end-to-end encrypted</span>
                    {isEditing ? (
                        <button type="button" disabled={selectedUsers.length === 0}
                                onClick={() => {
                                    const matrixIds = selectedUsers
                                        .map(user => getMatrixId(String(user.id)))
                                        .filter((id): id is string => !!id);
                                    if (matrixIds.length !== selectedUsers.length) {
                                        console.error('Some users do not have Matrix IDs');
                                    }
                                    onRoomCreate(matrixIds);
                                    onClose();
                                }}
                                className="h-10 rounded-[10px] bg-brand px-4 text-sm font-semibold text-white hover:bg-brand-hover disabled:bg-brand-disabled">
                            Add members
                        </button>
                    ) : (
                        <button type="button" disabled={selectedUsers.length === 0}
                                data-testid="chat.new-group.next-btn"
                                onClick={() => setGroupStep('settings')}
                                className="h-10 rounded-[10px] bg-brand px-4 text-sm font-semibold text-white hover:bg-brand-hover disabled:bg-brand-disabled">
                            Next
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CreateRoomModal;
